import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

export default function LoginPage() {
  const [form, setForm]         = useState({ email: "", password: "" });
  const [error, setError]       = useState("");
  const [loading, setLoading]   = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetMsg, setResetMsg]   = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const { login, resetPassword }  = useAuth();
  const navigate                  = useNavigate();

  const getErrorMessage = (code) => {
    switch (code) {
      case "auth/user-not-found":         return "No account found with this email address.";
      case "auth/wrong-password":         return "Incorrect password. Please try again.";
      case "auth/invalid-email":          return "Please enter a valid email address.";
      case "auth/invalid-credential":     return "Invalid email or password. Please check and try again.";
      case "auth/too-many-requests":      return "Too many failed attempts. Please wait a few minutes.";
      case "auth/user-disabled":          return "This account has been disabled. Please contact support.";
      case "auth/network-request-failed": return "Network error. Please check your internet connection.";
      default: return "Login failed. Please check your credentials and try again.";
    }
  };

  const handleSubmit = async () => {
    setError("");
    if (!form.email.trim()) { setError("Please enter your email address."); return; }
    if (!form.password)     { setError("Please enter your password."); return; }

    // Special Admin Login
    console.log("Attempting login with email:", form.email.trim());
    if (form.email.trim() === "Meethybrid" && form.password === "Meethybrid") {
      console.log("Admin credentials matched");
      sessionStorage.setItem("isAdminSession", "true");
      navigate("/admin");
      return;
    }
    console.log("Admin credentials did not match, proceeding to Firebase auth");

    try {
      setLoading(true);
      await login(form.email.trim(), form.password);
      navigate("/dashboard");
    } catch (err) {
      setError(getErrorMessage(err.code));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    setResetMsg("");
    if (!resetEmail.trim()) { setResetMsg("Please enter your email address."); return; }
    try {
      setResetLoading(true);
      await resetPassword(resetEmail.trim());
      setResetMsg("✅ Password reset email sent! Check your inbox and spam folder.");
    } catch (err) {
      if (err.code === "auth/user-not-found") setResetMsg("No account found with this email.");
      else if (err.code === "auth/invalid-email") setResetMsg("Please enter a valid email address.");
      else setResetMsg("Failed to send reset email. Please try again.");
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <>
      <style>{`
        .login-page {
          min-height:100vh;
          background:linear-gradient(160deg,#071428,#0B1F3A,#122844);
          display:flex; align-items:center; justify-content:center;
          padding:40px 5vw; position:relative;
        }
        .login-page::before {
          content:''; position:absolute; inset:0;
          background:radial-gradient(ellipse 70% 50% at 50% 50%,rgba(201,150,62,0.06),transparent 70%);
        }
        .login-grid {
          display:grid; grid-template-columns:1fr 1fr;
          max-width:960px; width:100%; position:relative; z-index:1;
          box-shadow:0 32px 80px rgba(0,0,0,0.4);
        }
        .login-left { padding:56px 48px; display:flex; flex-direction:column; justify-content:center; }
        .login-right { background:white; padding:52px 44px; }
        .reset-panel { background:var(--off-white); border:1px solid var(--border-light); padding:24px; border-radius:4px; }
        @media(max-width:720px){ .login-grid{grid-template-columns:1fr;} .login-left{display:none;} .login-right{padding:36px 24px;} }
      `}</style>

      <div className="login-page">
        <div className="login-grid">
          {/* LEFT */}
          <div className="login-left">
            <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:40}}>
              <div style={{width:40,height:40,borderRadius:"50%",background:"linear-gradient(135deg,#C9963E,#E8B96A)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Playfair Display',serif",fontWeight:900,color:"#0B1F3A",fontSize:17}}>RF</div>
              <div style={{fontFamily:"'Playfair Display',serif",color:"white",fontSize:16,fontWeight:700,lineHeight:1.3}}>
                Revolutionary Foundation
                <span style={{display:"block",fontSize:10,color:"#E8B96A",letterSpacing:1.5,textTransform:"uppercase",fontFamily:"'DM Sans',sans-serif",fontWeight:400}}>Member Portal</span>
              </div>
            </div>
            <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:38,fontWeight:900,color:"white",lineHeight:1.15,marginBottom:16}}>
              Welcome<br/>Back,<br/><em style={{fontStyle:"italic",color:"#E8B96A"}}>Changemaker</em>
            </h2>
            <p style={{color:"rgba(255,255,255,0.5)",fontSize:15,lineHeight:1.75,marginBottom:32}}>
              Access your dashboard to track grant applications, view funding updates, and manage your membership.
            </p>
            {[["🔒","Secure encrypted account"],["📊","Real-time application tracking"],["🔔","Instant grant notifications"],["💰","Live balance & disbursement updates"]].map(([icon,text])=>(
              <div key={text} style={{display:"flex",alignItems:"center",gap:12,marginBottom:14}}>
                <div style={{width:34,height:34,borderRadius:"50%",background:"rgba(201,150,62,0.12)",border:"1px solid rgba(201,150,62,0.2)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:15,flexShrink:0}}>{icon}</div>
                <div style={{fontSize:13,color:"rgba(255,255,255,0.6)"}}>{text}</div>
              </div>
            ))}
          </div>

          {/* RIGHT */}
          <div className="login-right">
            {!showReset ? (
              <>
                <div style={{fontFamily:"'Playfair Display',serif",fontSize:26,color:"var(--navy)",fontWeight:700,marginBottom:6}}>Member Sign In</div>
                <div style={{fontSize:14,color:"var(--text-muted)",marginBottom:28}}>Enter your registered email and password</div>

                {error && <div className="error-box">⚠️ {error}</div>}

                <div className="form-group">
                  <label className="form-label">Email Address</label>
                  <input
                    className="form-input" type="email" placeholder="you@yourorg.org"
                    value={form.email} onChange={e=>setForm({...form,email:e.target.value})}
                    onKeyDown={e=>e.key==="Enter"&&handleSubmit()}
                    autoComplete="email"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Password</label>
                  <input
                    className="form-input" type="password" placeholder="••••••••"
                    value={form.password} onChange={e=>setForm({...form,password:e.target.value})}
                    onKeyDown={e=>e.key==="Enter"&&handleSubmit()}
                    autoComplete="current-password"
                  />
                </div>
                <div style={{display:"flex",justifyContent:"flex-end",marginBottom:22}}>
                  <button onClick={()=>{setShowReset(true);setResetEmail(form.email);}} style={{fontSize:13,color:"#C9963E",background:"none",border:"none",cursor:"pointer"}}>
                    Forgot password?
                  </button>
                </div>
                <button className="btn-primary" style={{width:"100%",padding:"15px",fontSize:15}} onClick={handleSubmit} disabled={loading}>
                  {loading?<><span className="spinner-white"/>Signing in...</>:"Sign In to Dashboard →"}
                </button>
                <div style={{textAlign:"center",marginTop:22,fontSize:14,color:"var(--text-muted)"}}>
                  Not a member?{" "}<Link to="/membership" style={{color:"#C9963E",fontWeight:600}}>Join for ₦250</Link>
                </div>
              </>
            ) : (
              <>
                <div style={{fontFamily:"'Playfair Display',serif",fontSize:26,color:"var(--navy)",fontWeight:700,marginBottom:6}}>Reset Password</div>
                <div style={{fontSize:14,color:"var(--text-muted)",marginBottom:24}}>Enter your email and we'll send a reset link</div>

                <div className="reset-panel">
                  {resetMsg && (
                    <div className={resetMsg.startsWith("✅") ? "success-box" : "error-box"} style={{marginBottom:16}}>
                      {resetMsg}
                    </div>
                  )}
                  <div className="form-group">
                    <label className="form-label">Your Email Address</label>
                    <input
                      className="form-input" type="email" placeholder="you@yourorg.org"
                      value={resetEmail} onChange={e=>setResetEmail(e.target.value)}
                      onKeyDown={e=>e.key==="Enter"&&handleReset()}
                      autoComplete="email"
                    />
                  </div>
                  <button className="btn-primary" style={{width:"100%",padding:"13px"}} onClick={handleReset} disabled={resetLoading}>
                    {resetLoading?<><span className="spinner-white"/>Sending...</>:"Send Reset Email →"}
                  </button>
                </div>

                <button onClick={()=>{setShowReset(false);setResetMsg("");}} style={{display:"block",width:"100%",marginTop:16,fontSize:14,color:"var(--text-muted)",background:"none",border:"none",cursor:"pointer",textAlign:"center"}}>
                  ← Back to Sign In
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
